/**
 * Base API Service
 * Abstract base class providing common CRUD operations for all domain services
 */
import apiClient from '../apiClient';

/**
 * Valid types for query parameter values
 */
export type QueryParamValue = string | number | boolean | string[] | null | undefined;

/**
 * Abstract base class for all domain API services
 * Provides common CRUD methods and utilities
 * Uses apiClient for HTTP requests with MSAL authentication
 */
abstract class BaseApiService {
  /**
   * Perform GET request
   * @param url - API endpoint URL
   * @param params - Optional query parameters
   * @returns Promise with typed response data
   */
  protected async get<T>(url: string, params?: object): Promise<T> {
    return apiClient.get<T>(url, params);
  }

  /**
   * Perform GET request returning a binary Blob (e.g. file/CSV download).
   * @param url - API endpoint URL
   * @param params - Optional query parameters
   * @returns Promise resolving to the response Blob
   */
  protected async getBlob(url: string, params?: object): Promise<Blob> {
    return apiClient.getBlob(url, params);
  }

  /**
   * Perform POST request
   * @param url - API endpoint URL
   * @param data - Request body data
   * @returns Promise with typed response data
   */
  protected async post<T>(url: string, data?: object): Promise<T> {
    return apiClient.post<T>(url, data);
  }

  /**
   * Perform PUT request
   * @param url - API endpoint URL
   * @param data - Request body data
   * @returns Promise with typed response data
   */
  protected async put<T>(url: string, data?: object): Promise<T> {
    return apiClient.put<T>(url, data);
  }

  /**
   * Perform DELETE request
   * @param url - API endpoint URL
   * @param data - Optional request body data
   * @returns Promise with typed response data
   */
  protected async patch<T>(url: string, data?: object): Promise<T> {
    return apiClient.patch<T>(url, data);
  }

  protected async delete<T>(url: string, data?: unknown): Promise<T> {
    return apiClient.delete<T>(url, data);
  }

  /**
   * Build URLSearchParams from filters object
   * Filters out undefined, null, and empty string values
   * @param filters - Object with filter key-value pairs
   * @returns URLSearchParams instance
   */
  protected buildQueryParams(filters: Record<string, QueryParamValue> | object): URLSearchParams {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0 && value.every((item) => typeof item === 'string')) {
          params.append(key, value.join(','));
        }
        return;
      }

      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      ) {
        params.append(key, value.toString());
      }
    });

    return params;
  }
}

export default BaseApiService;
